import { Path, useForm } from "react-hook-form";
import { FormText } from "../common/forms/FormText";
import useDisplayMessage from "../common/lib/hooks/useDisplayMessage";
import KaAlert from "../common/lib/KaAlert";
import Container, { Progress } from "../common/Container";
import Footer from "../common/Footer";
import FooterButton from "../common/FooterButton";
import useGetOutboundStagedOrder, { GetOutboundStagedOrderResponse, OutboundStagedOrder, OutboundStagedPurchaseOrder } from "../requests/useGetOutboundStagedOrder";
import useIsAnonymous from "../common/lib/hooks/useIsAnonymous";
import { driverFacingErrorMessage, driverFacingMessage } from "../common/userFacingMessages/userFacingMessages";
import { useLingui } from "@lingui/react";
import KaModal from "../common/KaModal";
import Button from "../common/Button";
import { useState } from "react";
import { FooterProps } from "./StageOrderFlow/StageOrderTypes";
import StartOverButton from "../common/StartOverButton";

interface StagedOrderNumberProps extends FooterProps {
  siteId: string;
  kioskId: string;
  progress?: Progress;
  initialOrderNumber?: string;
  stagedOrderReceived: (stagedOrder: OutboundStagedOrder) => void;
  stagedPurchaseOrderReceived: (stagedPurchaseOrder: OutboundStagedPurchaseOrder) => void;
  startOver: () => void;
}

interface Form {
  number: string;
}

const StagedOrderNumber = (props: StagedOrderNumberProps) => {
  const { _ } = useLingui();
  const isAnonymous = useIsAnonymous();
  const displayMessage = useDisplayMessage();
  const [bothResponse, setBothResponse] = useState<GetOutboundStagedOrderResponse | undefined>();

  const { register, handleSubmit, setValue, formState, watch } = useForm<Form>({
    defaultValues: { number: props.initialOrderNumber ?? "" }
  });

  const orderNumber = watch("number");

  const formProps = (name: Path<Form>) => ({
    name,
    setValue,
    register,
    errors: formState.errors
  });

  const getOrder = useGetOutboundStagedOrder(props.siteId, orderNumber, props.kioskId, {
    onSuccess: (resp) => {
      if (resp.stagedOrder && resp.stagedPurchaseOrder) {
        setBothResponse(resp);
        return;
      }

      if (resp.stagedOrder) {
        outboundForStagedOrder(resp.stagedOrder);
        return;
      }

      outboundForStagedPurchaseOrder(resp.stagedPurchaseOrder);
    },
    onError: (err) => displayMessage.fail(_(driverFacingErrorMessage(err))),
  });

  const outboundForStagedOrder = (stagedOrder: OutboundStagedOrder) => {
    if (stagedOrder?.errorCode != undefined) {
      const params = stagedOrder.errorParameters ?? [];
      displayMessage.fail(_(driverFacingMessage(stagedOrder.errorCode, params)));
      setBothResponse(undefined);
    } else {
      props.stagedOrderReceived(stagedOrder);
    }
  }

  const outboundForStagedPurchaseOrder = (stagedPurchaseOrder: OutboundStagedPurchaseOrder) => {
    if (stagedPurchaseOrder?.errorCode != undefined) {
      const params = stagedPurchaseOrder.errorParameters ?? [];
      displayMessage.fail(_(driverFacingMessage(stagedPurchaseOrder.errorCode, params)));
      setBothResponse(undefined);
    } else {
      props.stagedPurchaseOrderReceived(stagedPurchaseOrder);
    }
  }

  return <Container
    progress={props.progress}
    title="Load Information"
    subtitle="What's your order number?">
    <KaModal
      isOpen={!!bothResponse}
      toggle={() => { }}
      title={"Select Direction"}
      className='modal-dialog-centered'
      body={(<>
        <div className="modal-body justify-content-between row">
          <Button className="col-5" onClick={() => outboundForStagedOrder(bothResponse!.stagedOrder)}>
            Picked up product
          </Button>
          <Button className="col-5" onClick={() => outboundForStagedPurchaseOrder(bothResponse!.stagedPurchaseOrder)}>
            Dropped off product
          </Button>
        </div>
      </>)} 
    />
    {isAnonymous && <StartOverButton startOver={props.startOver} />}
    <div className="w-75 m-auto">
      <KaAlert displayMessage={displayMessage.message} onClose={displayMessage.clear} />
      <FormText {...formProps("number")}
        displayName="Enter Order Number"
        placeholder="Enter order number"
        autoComplete={!isAnonymous}
        options={{
          required: 'Order Number is required',
          validate: {
            length: v => v.length < 51 || 'Order Number cannot be longer than 50 characters'
          },
        }}
      />
    </div>
    <Footer 
      backButton={<FooterButton {...props.backButton} />}
      nextButton={
        <FooterButton
          onClick={handleSubmit(() => getOrder.request())}
          spinning={getOrder.isLoading}
          disabled={!orderNumber}>
          Next
        </FooterButton>
    } />
  </Container>
}

export default StagedOrderNumber;
